import React, { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Form,
    Input,
    Label,
    Row,
    Button,
    Modal,
    ModalBody,
    ModalHeader,
    Spinner,
    CardHeader,
    CardFooter,
    Table,
    FormGroup,
    Badge,
    FormFeedback,
} from "reactstrap";


import {
    useGetRiskAssessmentByNumberQuery,
    useCreateRiskAssessmentMutation,
    useUpdateRiskAssessmentPermitMutation,
    useGetWorkActivityReportQuery,
    useGetCheckListQuestionnaireKeysQuery,
    useUpdateCheckListQuestionnaireByPermitNoMutation,
    useCreateRiskMigrationMutation,
    useUpdateRiskMigrationMutation,
    useGetEndShiftQuestionnaireKeysQuery,
    useUpdateEndShiftQuestionnaireByPermitNoMutation,
    useCreateRiskObservationMutation,
    useUpdateRiskObservationMutation,
    useCheckRiskAssessmentStatusMutation
} from "slices/riskAssements/riskAssementsSlice";
import classNames from "classnames";
import BreadCrumb from "Components/Common/BreadCrumb";
import Dropzone from "react-dropzone";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGetEmployeeDropDownByProjectQuery } from "slices/employee/employeeSlice";
import { useGetprojectByIdQuery } from "slices/projects/projectSlice";
import { useFormik } from "formik";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import * as Yup from "yup";
import moment from "moment";
import { number } from "prop-types";
import { formatDateTime } from "helpers/date_utils";
import { getStatusClass } from "utils/CommonUtils";
import { useGetCompaniesQuery } from "slices/InspectionReport/InspectionReportSlice";

// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

import { FreeMode, Navigation } from "swiper/modules";
import { Swiper } from "swiper/react";
import { SwiperSlide } from "swiper/react";
import { getErrorText } from "helpers/error_message";
import ValidationAlertStatic from "pages/SafetyApp/ValidationAlertStatic";
import { toast } from "react-toastify";
import RiskAssessmentSubmit from "./RiskAssessmentSubmitButton";

type CreateReportResult = {
    id: number;
    error?: FetchBaseQueryError;
};


// Define the types for form data
type FormData = {
    parent_company: number;
    project: number;
    work_activity: string;
    start_date: string;
    end_date: string;
    due_date: string;
    report_status: number;
    supervised_by_employee_uuid: string;
};

export interface InspectionReportRowDetail {
    inspection_report: number;
    inspection_sub_company: string;
    location: string;
    name_of_person_incharge: number;
    designation: string;
    comments_report1: string | null;
    attachment1: string | null;
    inspection_row_id: number | null;
    work_assigned: string,
    is_bad_practice: boolean,
    observation: string,
    recommendation: string,
    corrective_actions: string,
    preventive_actions: string,
}

interface Question {
    questionnaire_key: number;
    question: string;
    answer: string;
    sort_id: number;
}

const RiskAssessmentForm: React.FC = () => {
    const { project, reportNo: reportNoFromURL } = useParams<{
        project: string;
        reportNo?: string;
    }>();
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [observationModalOpen, setObservationModalOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState({});
    const [viewReport, setViewReport] = useState<boolean>(false);
    const [checkListView, setCheckListView] = useState<boolean>(false);
    const [endShiftListView, setEndShiftListView] = useState<boolean>(false);
    const [reportId, setReportId] = useState<string | null>(null);
    const [editingReportId, setEditingReportId] = useState<number | null>(null);
    const [modalFormData, setModalFormData] = useState<any>({});

    const [searchParams, setSearchParams] = useSearchParams();
    const [geoPermission, setGeoPermission] = React.useState<boolean>(false);
    const [modalBackDrop, setModalBackDrop] = useState<boolean>(false);
    const [cardView, setCardView] = useState<boolean>(false);
    const [validationMessages, setValidationMessages] = useState<string[]>([]);
    const [location, setLocation] = useState<{
        latitude: number | null;
        longitude: number | null;
    }>({ latitude: null, longitude: null });

    const [initialQuestions, setInitialQuestions] = useState<Question[]>([]);
    const [initialEndShiftQuestions, setInitialEndShiftQuestions] = useState<Question[]>([]);


    const { data: projectData, isLoading: projectLoading } = useGetprojectByIdQuery(
        project, { skip: !project }
    );

    const [checkRiskAssessment]= useCheckRiskAssessmentStatusMutation();

    const {
        data: reportData,
        isLoading,
        isError: error,
        refetch: reportDataRefetch,
    } = useGetRiskAssessmentByNumberQuery(reportId || 0, { skip: !reportId });
    // Fetch questionnaire data
    const { data: questionnaireData, isLoading: questionLoading } =
        useGetCheckListQuestionnaireKeysQuery(undefined);
    const [updatePermitQuestionnaireKey, { isLoading: patchisLoading }] =
        useUpdateCheckListQuestionnaireByPermitNoMutation();

    //fetch End Shift Questionnaire Data

    const { data: endShiftQuestionnaireData, isLoading: endShiftquestionLoading } =
        useGetEndShiftQuestionnaireKeysQuery(undefined);
    const [updateEndShiftQuestionnaireKey, { isLoading: patchisEndShiftLoading }] =
        useUpdateEndShiftQuestionnaireByPermitNoMutation();




    // Populate initialQuestions state once questionnaireData is available
    useEffect(() => {
        if (questionnaireData) {
            const sortedQuestions = questionnaireData
                .map((q: any) => ({
                    checklist_questionnaires: q.id,
                    question: q.question,
                    answer: "",
                    sort_id: q.sort_id, // Include sort_id in the mapped questions
                }))
                .sort(
                    (a: { sort_id: number }, b: { sort_id: number }) =>
                        a.sort_id - b.sort_id,
                );

            setInitialQuestions(sortedQuestions);
        }
    }, [questionnaireData]);


    useEffect(() => {
        if (reportData?.checklist_questionnaire?.length) {
            const updatedQuestions = reportData.checklist_questionnaire.map((q: any) => ({
                checklist_questionnaires: q.id, // Ensure you're using the correct key
                question: q.question,
                answer: q.answer || "", // Use existing answer or set it empty
            }));
            Questionnaireformik.setValues({ questions: updatedQuestions });
        }
    }, [reportData]);


    // Populate initialQuestions state once questionnaireData is available
    useEffect(() => {
        if (endShiftQuestionnaireData) {
            const sortedQuestions = endShiftQuestionnaireData
                .map((q: any) => ({
                    end_of_shift_questionnaires: q.id,
                    question: q.question,
                    answer: "",
                    sort_id: q.sort_id, // Include sort_id in the mapped questions
                }))
                .sort(
                    (a: { sort_id: number }, b: { sort_id: number }) =>
                        a.sort_id - b.sort_id,
                );

            setInitialEndShiftQuestions(sortedQuestions);
        }
    }, [endShiftQuestionnaireData]);


    useEffect(() => {
        if (reportData?.end_of_shift_questionnaire?.length) {
            const updatedQuestions = reportData.end_of_shift_questionnaire.map((q: any) => ({
                end_of_shift_questionnaire: q.id, // Ensure you're using the correct key
                question: q.question,
                answer: q.answer || "", // Use existing answer or set it empty
            }));
            EndShiftQuestionnaireformik.setValues({ questions: updatedQuestions });
        }
    }, [reportData]);



    useEffect(() => {
        // Check if create_task is true in the URL search params
        if (searchParams.get("view_report") === "true") {
            setCardView(true);
            // Update the URL to set create_task to false
            searchParams.set("view_report", "true");
            setSearchParams(searchParams);
        }
    }, [searchParams, setSearchParams]);



    const { data: workActivityData } = useGetWorkActivityReportQuery(undefined);
    const { data: companiesData } = useGetCompaniesQuery();

    const [createReport, { isLoading: isCreating }] = useCreateRiskAssessmentMutation();
    const [createMitigatingReportbyId, { isLoading: isCreatingbyId }] = useCreateRiskMigrationMutation();

    const [createRiskObservationReportbyId, { isLoading: isObservationCreatingbyId }] = useCreateRiskObservationMutation();
    const [updateRiskObservationReport, { isLoading: patchRiskObservationRowByIdloading }] = useUpdateRiskObservationMutation();


    const [updateReport, { isLoading: isUpdating }] = useUpdateRiskAssessmentPermitMutation();
    const [
        updateMitigatingReport,
        { isLoading: patchReportRowByIdloading },
    ] = useUpdateRiskMigrationMutation();

    useEffect(() => {
        if (reportNoFromURL) {
            setReportId(reportNoFromURL)
            setViewReport(true)
        }
    }, [reportNoFromURL]);


    useEffect(() => {
        // Retrieve the authUser object from sessionStorage and parse it
        const authUser = localStorage.getItem("authUser");
        if (authUser) {
            try {
                const parsedAuthUser = JSON.parse(authUser);
                console.log('parsedAuthUser', parsedAuthUser)
                const employeeUUID = parsedAuthUser.employee_id || "";
                console.log('employeeUUID', employeeUUID)
                setCurrentUser(parsedAuthUser);
                if (employeeUUID) {
                    formik.setFieldValue("supervised_by", employeeUUID);
                    formik.setFieldValue("report_status", 1);
                }
            } catch (error) {
                console.error("Failed to parse authUser from local storage:", error);
            }
        }
    }, []);

    const getLocation = () => {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLocation({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    });
                    setGeoPermission(true);
                },
                (err) => {
                    setValidationMessages((prev) => {
                        const errorMessage = getErrorText("location-disabled");
                        return prev.includes(errorMessage) ? prev : [...prev, errorMessage];
                    });
                    setModalBackDrop(true);
                    setGeoPermission(false);
                },
                { enableHighAccuracy: true },
            );
        } else {
            setValidationMessages((prev) => {
                const errorMessage = getErrorText("location-not-supported");
                return prev.includes(errorMessage) ? prev : [...prev, errorMessage];
            });
            setModalBackDrop(true);
            setGeoPermission(false);
        }
    };

    const validateToolBoxMeeting = async () => {
        const checkStatus: any = await checkRiskAssessment({
          project: project,
        });
        console.log(checkStatus);
    
        if (checkStatus.data.length !== 0) {
          setValidationMessages((prev: any) => {
            const existingKeys = new Set(prev.map((message: any) => message.key)); // Track existing keys
            const newMessages: any[] = [];
    
            checkStatus.data.forEach((report: any) => {
              const permitNo = report.assessment_id;
              const permitStatus = report.report_status;
    
              // Generate a unique key for this report
              const errorMessageKey = `${permitNo}-${permitStatus}`;
    
              // Add error message only if the key doesn't already exist
              if (
                !existingKeys.has(errorMessageKey) &&
                reportNoFromURL !== permitNo &&
                (permitStatus !== 'Draft')
              ) {
                const errorMessage = (
                  <span key={errorMessageKey}>
                    {getErrorText("report-status-exist")}{" "}
                    <b>
                      {permitNo} - {permitStatus}
                    </b>
                  </span>
                );
                newMessages.push(errorMessage);
              }
            });
    
            // If there are new messages, show the modal
            if (newMessages.length > 0) {
              setModalBackDrop(true);
            }
    
            // Return the combined array of previous and new messages
            return [...prev, ...newMessages];
          });
        }
      };

    useEffect(() => {
        setValidationMessages([]);
        getLocation();
        validateToolBoxMeeting();
    }, [project]);

    const Questionnaireformik = useFormik({
        initialValues: {
            questions: initialQuestions,
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            questions: Yup.array().of(
                Yup.object({
                    answer: Yup.string().required("Answer is required"),
                }),
            ),
        }),
        validateOnChange: false, // Disable validation on change
        validateOnBlur: false, // Disable validation on blur
        onSubmit: async (values) => {
            try {
                const payload = {
                    questionnaire_updates: values.questions,
                };

                await updatePermitQuestionnaireKey({
                    assessment_id: reportId,
                    updatedQuestionnaireKeyData: payload,
                }).unwrap();
                reportDataRefetch();
                setCheckListView(true);

            } catch (error) {
                console.error("Error updating questionnaire", error);
            }
        },
    });

    const EndShiftQuestionnaireformik = useFormik({
        initialValues: {
            questions: initialEndShiftQuestions,
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            questions: Yup.array().of(
                Yup.object({
                    answer: Yup.string().required("Answer is required"),
                }),
            ),
        }),
        validateOnChange: false, // Disable validation on change
        validateOnBlur: false, // Disable validation on blur
        onSubmit: async (values) => {
            try {
                const payload = {
                    questionnaire_updates: values.questions,
                };

                await updateEndShiftQuestionnaireKey({
                    assessment_id: reportId,
                    updatedQuestionnaireKeyData: payload,
                }).unwrap();
                reportDataRefetch();
                setEndShiftListView(true);

                // await updatePermit({
                //   hw_permit_no: hwPermitNo,
                //   updatedPermitData: {
                //     questionnaire_comments: values.comments,
                //   },
                // }).unwrap();

                // nextStep();
            } catch (error) {
                console.error("Error updating questionnaire", error);
            }
        },
    });



    const formik = useFormik({
        initialValues: {
            project: Number(project),
            work_activity: reportData?.work_activity,
            no_of_employees: reportData?.no_of_employees,
            report_date: reportData?.report_date,
        },
        validationSchema: Yup.object({

            work_activity: Yup.string()
                .required("Work Activity is required"),

            no_of_employees: Yup.string().required("Number of Employees is required"),
            report_date: Yup.string().required('Report Date is required')
        }),

        onSubmit: async (values) => {
            try {
                const payload = {
                    ...values,
                    work_activity: values.work_activity,
                    project: Number(project),
                    // location: location,
                    latitude: location.latitude,
                    longitude: location.longitude,
                };
                let response;

                console.log('payload', payload)

                if (reportId) {
                    response = await updateReport({
                        id: reportId,
                        reportData: payload,
                    }).unwrap();
                    setViewReport(true)
                } else {
                    // Create new report
                    response = await createReport(payload).unwrap();
                    setViewReport(true)
                    navigate(
                        `/assessment-report/${project}/${response.assessment_id}`,
                    );
                    //   setActiveTab(activeTab + 1);
                    console.log("CreateReportResult", response);

                    if (response) {
                        console.log("Report created with ID:", response.assessment_id);
                        setReportId(response?.assessment_id);
                        reportDataRefetch();
                        setViewReport(true)
                        navigate(
                            `/assessment-report/${project}/${response.assessment_id}`,
                        );
                    }
                }
                // setActiveTab(activeTab + 1);
            } catch (error) {
                console.error("Error updating or creating report:", error);
                toast.error("Failed to update Inspection Report!");
            }
        }
    });

    useEffect(() => {
        if (!isLoading && reportData) {
            const {
                work_activity,
                project,
                no_of_employees,
                report_date,
            } = reportData;

            formik.setValues({
                work_activity:work_activity || "",
                project: project,
                no_of_employees: no_of_employees || "",
                report_date: report_date || ""
            });
        }
    }, [isLoading, reportData, project]);

    const { data: employeeData } = useGetEmployeeDropDownByProjectQuery(
        formik.values.project, { skip: !formik.values.project }
    );

    const handleMitigatingButtonClick = () => {
        practiceFormik.resetForm();
        setModalOpen(true);
    };


    const toggleModal = () => {
        setModalOpen(!modalOpen);
    };


    const observationToggleModal = () => {
        setObservationModalOpen(!observationModalOpen);
    };

    console.log('modalFormData---', modalFormData)

    useEffect(() => {
        if (modalFormData) {
            practiceFormik.setValues({
                activity: modalFormData.activity || "",
                activity_time: formatDateTime(modalFormData.activity_time, 'time') || "",
                hazard: modalFormData.hazard || "",
                // hazard_time: formatDateTime(modalFormData.hazard_time, 'time') || "",
                safety_provisions: modalFormData.safety_provisions || "",
                // safety_provisions_time: formatDateTime(modalFormData.safety_provisions_time, 'time') || "",
            });
        }
    }, [modalFormData]);

    const practiceFormik = useFormik({
        initialValues: {
            activity: "",
            activity_time: "",
            hazard: "",
            // hazard_time: "",
            safety_provisions: "",
            // safety_provisions_time: "",
        },
        validationSchema: Yup.object({
            activity: Yup.string().required('Activity is required.'),
            activity_time: Yup.string()
                .required('Activity Time is required.')
                .matches(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Time must be in HH:MM format'),
            hazard: Yup.string().required('Hazard is required.'),
            // hazard_time: Yup.string()
            //     .required('Hazard Time is required.')
            //     .matches(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Time must be in HH:MM format'),
            safety_provisions: Yup.string().required('Safety Provisions is required.'),
            // safety_provisions_time: Yup.string()
            //     .required('Safety Provisions Time is required.')
            //     .matches(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Time must be in HH:MM format'),
        }),

        onSubmit: async (values) => {
            try {
                const mitigationReport = {
                    ...values,
                    report_status: 3, // Assuming this is needed for your API
                };

                if (editingReportId) {
                    await updateMitigatingReport({
                        id: editingReportId,
                        reportData: mitigationReport,
                    }).unwrap();
                } else {
                    await createMitigatingReportbyId({
                        assessment_id: reportId,
                        mitigationReport
                    }).unwrap();
                }

                toggleModal();
                reportDataRefetch();
                setEditingReportId(null);
                practiceFormik.resetForm();
            } catch (error) {
                console.error("Error saving mitigation report:", error);
                toast.error("Failed to save Mitigation Report!");
            }
        }
    });


    useEffect(() => {
        if (!isLoading && modalFormData) {
            const { observation, action_required } = modalFormData;
            observationFormik.setValues({
                observation: observation || "",
                action_required: action_required || "",
            });
        }
    }, [isLoading, modalFormData, project]);

    const observationFormik = useFormik({
        initialValues: {
            observation: "",
            action_required: "",
        },
        validationSchema: Yup.object({
            observation: Yup.string().required('Observation is required'),
            action_required: Yup.string().required('Action required is required'),
        }),
        onSubmit: async (values) => {
            try {
                const observationData = {
                    observation: values.observation,
                    action_required: values.action_required,
                };

                let response;
                if (editingReportId) {
                    response = await updateRiskObservationReport({
                        id: editingReportId,
                        observationData: observationData,
                    }).unwrap();
                } else {
                    response = await createRiskObservationReportbyId({ assessment_id: reportId, observationData }).unwrap();
                }

                observationToggleModal();
                reportDataRefetch();
                setEditingReportId(null);
                observationFormik.resetForm();
                toast.success("Risk observation saved successfully!");
            } catch (error) {
                console.error("Error saving risk observation:", error);
                toast.error("Failed to save risk observation!");
            }
        }
    });




    if (isLoading) {
        return (
            <React.Fragment>
                <Container fluid>
                    <div className="page-content" style={{ height: "80vh" }}>
                        {" "}
                        {/* Full viewport height */}
                        <Container fluid className="h-100">
                            <Row className="h-100 d-flex justify-content-center align-items-center">
                                <Col lg={12} className="text-center">
                                    <Spinner color="primary"></Spinner> <br /> <br />
                                    <span className="text-muted">
                                        Fetching Risk Assessments Information...
                                    </span>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </Container>
            </React.Fragment>
        );
    }


    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Risk Assessment" breadcrumbItem="Risk Assessment" />
                <Row>
                    <Col lg={12}>
                        <Card>
                            {viewReport &&
                                <>
                                    <Card>
                                        <CardHeader className="d-flex align-items-between justify-content-between">
                                            <h4 className="mt-2">Risk Assessment Details</h4>
                                            {reportData?.report_status_name !== "Submitted" && !cardView && (
                                                <Button
                                                    color="primary"
                                                    className=" btn-sm"
                                                    onClick={() => { setViewReport(false) }}
                                                >
                                                    <i className="ri-pencil-fill fs-12 edit-task-status-class"></i> Edit
                                                </Button>
                                            )}

                                        </CardHeader>
                                        <CardBody>
                                            <div className="text-muted">

                                                <div className="pt-3 border-top border-top-dashed mt-4">
                                                    <Row>
                                                        <Col lg={3} sm={6}>
                                                            <div>
                                                                <p className="mb-2 text-uppercase fw-medium">
                                                                    Report ID :
                                                                </p>
                                                                <h5 className="fs-15 mb-0">
                                                                    {reportData?.assessment_id}
                                                                </h5>
                                                            </div>
                                                        </Col>
                                                        <Col lg={3} sm={6}>
                                                            <div>
                                                                <p className="mb-2 text-uppercase fw-medium">
                                                                    Project :
                                                                </p>
                                                                <h5 className="fs-15 mb-0">
                                                                    {reportData?.project_name}
                                                                </h5>
                                                            </div>
                                                        </Col>
                                                        <Col lg={3} sm={6}>
                                                            <div>
                                                                <p className="mb-2 text-uppercase fw-medium">
                                                                    Work Activity :
                                                                </p>
                                                                <h5 className="fs-15 mb-0">
                                                                    {reportData?.work_activity}
                                                                </h5>
                                                            </div>
                                                        </Col>
                                                        {/* Conditionally render Sub Company Name only if it exists */}
                                                        {reportData?.company_name && (
                                                            <Col lg={3} sm={6}>
                                                                <div>
                                                                    <p className="mb-2 text-uppercase fw-medium">
                                                                        Company Name :
                                                                    </p>
                                                                    <h5 className="fs-15 mb-0">
                                                                        {reportData.company_name}
                                                                    </h5>
                                                                </div>
                                                            </Col>
                                                        )}
                                                    </Row>
                                                </div>

                                                <div className="pt-3 border-top border-top-dashed mt-4">
                                                    <Row>
                                                        <Col lg={3} sm={6}>
                                                            <div>
                                                                <p className="mb-2 text-uppercase fw-medium">
                                                                    Sub-Contractor :
                                                                </p>
                                                                <h5 className="fs-15 mb-0">
                                                                    {reportData?.subcontractor}
                                                                </h5>
                                                            </div>
                                                        </Col>
                                                        <Col lg={3} sm={6}>
                                                            <div>
                                                                <p className="mb-2 text-uppercase fw-medium">
                                                                    No of Employees :
                                                                </p>
                                                                <h5 className="fs-15 mb-0">
                                                                    {reportData?.no_of_employees}
                                                                </h5>
                                                            </div>
                                                        </Col>
                                                        <Col lg={3} sm={6}>
                                                            <div>
                                                                <p className="mb-2 text-uppercase fw-medium">
                                                                    Reported Date:
                                                                </p>
                                                                <h5 className="fs-15 mb-0">
                                                                    {formatDateTime(reportData?.report_date, 'date')}
                                                                </h5>
                                                            </div>
                                                        </Col>
                                                        {/* Conditionally render Sub Company Name only if it exists */}
                                                        {reportData?.sub_company_name && (
                                                            <Col lg={3} sm={6}>
                                                                <div>
                                                                    <p className="mb-2 text-uppercase fw-medium">
                                                                        Sub Company Name :
                                                                    </p>
                                                                    <h5 className="fs-15 mb-0">
                                                                        {reportData.sub_company_name}
                                                                    </h5>
                                                                </div>
                                                            </Col>
                                                        )}
                                                    </Row>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </>
                            }
                            {!viewReport &&
                                <Card>
                                    <CardHeader className="d-flex align-items-between justify-content-between">
                                        <h4 className="mt-2">Risk Assessment Informations</h4>
                                    </CardHeader>
                                    <CardBody>
                                        <Form onSubmit={formik.handleSubmit}>
                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Label htmlFor="project_name" className="form-label">Project</Label>
                                                    <Input
                                                        type="text"
                                                        className="form-control"
                                                        id="project_name"
                                                        name="project_name"
                                                        value={projectData?.project_name || ""}
                                                        readOnly
                                                        disabled
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <Label htmlFor="supervised_by" className="form-label">Work Supervised By</Label>
                                                    <Input
                                                        type="text"
                                                        className="supervised_by"
                                                        id="supervised_by"
                                                        name="supervised_by"
                                                        //@ts-ignore
                                                        value={currentUser?.employee_full_name || ""}
                                                        readOnly
                                                        disabled
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Label htmlFor="work_activity" className="form-label">
                                                        Work Activity <span className="text-danger">*</span>
                                                    </Label>

                                                    <Input
                                                        type="text"
                                                        className="form-control"
                                                        id="work_activity"
                                                        name="work_activity"
                                                        value={formik.values.work_activity}
                                                        onChange={formik.handleChange}
                                                    />

                                                    {/* <Input
                                                        type="select"
                                                        className="form-select"
                                                        id="work_activity"
                                                        name="work_activity"
                                                        value={formik.values.work_activity}
                                                        onChange={(e) => {
                                                            formik.handleChange(e);
                                                            if (e.target.value === "other") {
                                                                formik.setFieldValue("other_inspection_type", "");
                                                            }
                                                        }}
                                                    >
                                                        <option value="">Select Work Activity</option>
                                                        {workActivityData?.map((type: any) => (
                                                            <option key={type.id} value={type.id}>
                                                                {type.name}
                                                            </option>
                                                        ))}
                                                    </Input> */}

                                                    {formik.touched.work_activity &&
                                                        formik.errors.work_activity ? (
                                                        <div className="text-danger">
                                                            {formik.errors.work_activity as string}
                                                        </div>
                                                    ) : null}

                                                </Col>
                                                <Col md={6}>
                                                    <Label htmlFor="no_of_employees" className="form-label">No of Employees<span className="text-danger">*</span></Label>
                                                    <Input
                                                        type="text"
                                                        className="form-control"
                                                        id="no_of_employees"
                                                        name="no_of_employees"
                                                        value={formik.values.no_of_employees}
                                                        onChange={formik.handleChange}
                                                    />
                                                    {formik.touched.no_of_employees &&
                                                        formik.errors.no_of_employees ? (
                                                        <div className="text-danger">
                                                            {formik.errors.no_of_employees as string}
                                                        </div>
                                                    ) : null}
                                                </Col>

                                                {/* <Col md={6}>
                                                    <Label htmlFor="Parent Company" className="form-label">Parent Company</Label>
                                                    <Input
                                                        type="text"
                                                        className=""
                                                        id="parent_company"
                                                        name="parent_company"
                                                        //@ts-ignore
                                                        value={companiesData?.[0]?.company_name || ""}
                                                        readOnly
                                                        disabled
                                                    />
                                                </Col> */}
                                            </Row>
                                            <Row className="mb-3">
                                            <Col md={4}>
                                                    <Label htmlFor="start_date" className="form-label">Reported Date <span className="text-danger">*</span></Label>
                                                    <Input
                                                        type="date"
                                                        className="form-control"
                                                        id="report_date"
                                                        name="report_date"
                                                        value={formik.values.report_date}
                                                        onChange={formik.handleChange}
                                                    />
                                                    {formik.touched.report_date &&
                                                        formik.errors.report_date ? (
                                                        <div className="text-danger">
                                                            {formik.errors.report_date as string}
                                                        </div>
                                                    ) : null}
                                                </Col>
                                            </Row>
                                            <div className="text-end">
                                                <Button color="primary" type="submit" disabled={isCreating || isUpdating}>
                                                    {isCreating || isUpdating ? <Spinner size="sm" /> : "Save Changes"}
                                                </Button>
                                            </div>
                                        </Form>
                                    </CardBody>
                                </Card>
                            }

                        </Card>

                        {reportId && <div>
                            {!checkListView &&
                                <Card className="mt-2">
                                    <CardHeader><h4>Pre-work Checks</h4></CardHeader>
                                    <CardBody>
                                        {isLoading || questionLoading ? (
                                            <Spinner color="primary" />
                                        )
                                            // : reportData?.checklist_questionnaire?.length === 0 ? (
                                            //     <div>No checklist questions available</div>
                                            // ) 
                                            :
                                            (
                                                <Form onSubmit={Questionnaireformik.handleSubmit}>
                                                    <Table className="table table-striped">
                                                        <thead>
                                                            <tr>
                                                                <th >Question</th>
                                                                <th>Answer</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {Questionnaireformik?.values?.questions.map((q, index) => (
                                                                <tr key={q.sort_id}>
                                                                    <td>{q.question}</td>
                                                                    <td>
                                                                        <FormGroup check inline>
                                                                            <Label check className="mr-2">
                                                                                <Input
                                                                                    type="radio"
                                                                                    name={`questions[${index}].answer`}
                                                                                    value="Yes"
                                                                                    checked={q.answer === "Yes"}
                                                                                    onChange={Questionnaireformik.handleChange}
                                                                                />
                                                                                Yes
                                                                            </Label>
                                                                        </FormGroup>
                                                                        <FormGroup check inline>
                                                                            <Label check>
                                                                                <Input
                                                                                    type="radio"
                                                                                    name={`questions[${index}].answer`}
                                                                                    value="No"
                                                                                    checked={q.answer === "No"}
                                                                                    onChange={Questionnaireformik.handleChange}
                                                                                />
                                                                                No
                                                                            </Label>
                                                                        </FormGroup>
                                                                        <FormGroup check inline>
                                                                            <Label check>
                                                                                <Input
                                                                                    type="radio"
                                                                                    name={`questions[${index}].answer`}
                                                                                    value="NA"
                                                                                    checked={q.answer === "NA"}
                                                                                    onChange={Questionnaireformik.handleChange}
                                                                                />
                                                                                N/A
                                                                            </Label>
                                                                        </FormGroup>
                                                                        {/* {Questionnaireformik.touched.questions &&
                                                                        Questionnaireformik.errors.questions &&
                                                                        Questionnaireformik.errors.questions[index] &&
                                                                        typeof Questionnaireformik.errors.questions[index] === "object" &&
                                                                        Questionnaireformik.errors.questions[index].answer && (
                                                                            <div className="text-danger">
                                                                                {
                                                                                    //@ts-ignore
                                                                                    Questionnaireformik.errors.questions[index].answer
                                                                                }
                                                                            </div>
                                                                        )} */}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>

                                                    <div className="d-flex justify-content-between mt-3">
                                                        <Button color="primary" type="submit" disabled={patchisLoading || isLoading}>
                                                            {patchisLoading ? "Loading..." : "Save"}
                                                        </Button>
                                                    </div>
                                                </Form>
                                            )}
                                    </CardBody>
                                </Card>
                            }

                            {checkListView &&
                                <Card className="mt-2">
                                    <CardHeader className="d-flex justify-content-between">
                                        <h4>Pre-work Checks</h4>

                                        {reportData?.report_status_name !== "Submitted" && !cardView && checkListView && (
                                            <Button
                                                className="btn btn-soft-info btn-md shadow-none"
                                                onClick={() => {
                                                    setCheckListView(true);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                        )}
                                    </CardHeader>
                                    <CardBody>
                                        {isLoading ? (
                                            <Spinner color="primary" />
                                        )
                                            // : reportData?.checklist_questionnaire?.length === 0 ? (
                                            //     <div>No checklist questions available</div>
                                            // ) 
                                            :
                                            (
                                                <table className="table table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th>Question</th>
                                                            <th>Answer</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reportData?.checklist_questionnaire?.map((q: any) => (
                                                            <tr key={q.id}>
                                                                <td>{q.question}</td>
                                                                <td>
                                                                    <Badge
                                                                        color={q.answer === "Yes" ? "success" : "danger"}
                                                                    >
                                                                        {q.answer}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>

                                            )}
                                    </CardBody>
                                </Card>
                            }

                            <Card>
                                <CardHeader className="d-flex justify-content-between">
                                    <h4>Critical Risk and Mitigating Measures(Perfom at least 3 checks per Shift)</h4>
                                    {reportData?.report_status_name !== "Submitted" && !cardView &&
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                practiceFormik.resetForm();
                                                setEditingReportId(null);
                                                toggleModal();
                                            }}
                                        >
                                            Add Mitigation
                                        </Button>
                                    }
                                </CardHeader>

                                <CardBody>
                                    {reportData?.risk_migration && reportData?.risk_migration?.length > 0 ? (
                                        <div className="d-flex flex-column gap-3">
                                            {reportData.risk_migration.map((item: any, index: number) => (
                                                <Card key={index}>
                                                    <CardBody>
                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                            <div>
                                                                <h4>{item?.activity}</h4>
                                                            </div>
                                                            {reportData?.report_status_name !== "Submitted" && !cardView &&

                                                                <Button
                                                                    color="info"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setModalFormData(item);
                                                                        setEditingReportId(item.id);
                                                                        toggleModal();
                                                                    }}
                                                                >
                                                                    Edit
                                                                </Button>
                                                            }
                                                        </div>

                                                        <Row>
                                                            <Col md={12}>
                                                                <div className="p-2 bg-light rounded mb-2 d-flex align-items-center justify-content-between">
                                                                    <div style={{ width: "50%" }}>
                                                                        <h6 className="mb-1"><strong>Activity</strong></h6>
                                                                        <p className="mb-1">{item.activity}</p>
                                                                    </div>
                                                                    <div style={{ width: "50%", textAlign: "center" }}>
                                                                        <b className="text-black">Time: {formatDateTime(item.activity_time, 'time')}</b>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col md={12}>
                                                                <div className="p-2 bg-light rounded mb-2 d-flex align-items-center justify-content-between ">
                                                                    <div style={{ width: "50%" }}>
                                                                        <h6 className="mb-1"><strong>Hazard</strong></h6>
                                                                        <p className="mb-1">{item.hazard}</p>
                                                                    </div>
                                                                    <div style={{ width: "50%", textAlign: "center" }}>
                                                                        {/* <b className="text-black">Time: {formatDateTime(item.hazard_time, 'time')}</b> */}
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col md={12}>
                                                                <div className="p-2 bg-light rounded mb-2 d-flex align-items-center justify-content-between">
                                                                    <div style={{ width: "50%" }}>
                                                                        <h6 className="mb-1"><strong>Safety Provisions</strong></h6>
                                                                        <p className="mb-1">{item.safety_provisions}</p>
                                                                    </div>
                                                                    <div style={{ width: "50%", textAlign: "center" }}>
                                                                        {/* <b className="text-black">Time: {formatDateTime(item.safety_provisions_time, 'time')}</b> */}
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </CardBody>
                                                </Card>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center">
                                            <p>No mitigation reports found.</p>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>

                            {!endShiftListView &&
                                <Card className="mt-2">
                                    <CardHeader><h4>End of Shift Review</h4></CardHeader>
                                    <CardBody>
                                        {isLoading || endShiftquestionLoading ? (
                                            <Spinner color="primary" />
                                        )
                                            // : reportData?.checklist_questionnaire?.length === 0 ? (
                                            //     <div>No checklist questions available</div>
                                            // ) 
                                            :
                                            (
                                                <Form onSubmit={EndShiftQuestionnaireformik.handleSubmit}>
                                                    <Table className="table table-striped">
                                                        <thead>
                                                            <tr>
                                                                <th >Question</th>
                                                                <th>Answer</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {EndShiftQuestionnaireformik?.values?.questions.map((q, index) => (
                                                                <tr key={q.sort_id}>
                                                                    <td style={{ width: "70%" }}>{q.question}</td>
                                                                    <td style={{ width: "30%" }}>
                                                                        <FormGroup check inline>
                                                                            <Label check className="mr-2">
                                                                                <Input
                                                                                    type="radio"
                                                                                    name={`questions[${index}].answer`}
                                                                                    value="Yes"
                                                                                    checked={q.answer === "Yes"}
                                                                                    onChange={EndShiftQuestionnaireformik.handleChange}
                                                                                />
                                                                                Yes
                                                                            </Label>
                                                                        </FormGroup>
                                                                        <FormGroup check inline>
                                                                            <Label check>
                                                                                <Input
                                                                                    type="radio"
                                                                                    name={`questions[${index}].answer`}
                                                                                    value="No"
                                                                                    checked={q.answer === "No"}
                                                                                    onChange={EndShiftQuestionnaireformik.handleChange}
                                                                                />
                                                                                No
                                                                            </Label>
                                                                        </FormGroup>
                                                                        <FormGroup check inline>
                                                                            <Label check>
                                                                                <Input
                                                                                    type="radio"
                                                                                    name={`questions[${index}].answer`}
                                                                                    value="NA"
                                                                                    checked={q.answer === "NA"}
                                                                                    onChange={EndShiftQuestionnaireformik.handleChange}
                                                                                />
                                                                                N/A
                                                                            </Label>
                                                                        </FormGroup>
                                                                        {/* {EndShiftQuestionnaireformik.touched.questions &&
                                                                        EndShiftQuestionnaireformik.errors.questions &&
                                                                        EndShiftQuestionnaireformik.errors.questions[index] &&
                                                                        typeof EndShiftQuestionnaireformik.errors.questions[index] === "object" &&
                                                                        EndShiftQuestionnaireformik.errors.questions[index].answer && (
                                                                            <div className="text-danger">
                                                                                {
                                                                                    //@ts-ignore
                                                                                    EndShiftQuestionnaireformik.errors.questions[index].answer
                                                                                }
                                                                            </div>
                                                                        )} */}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>

                                                    <div className="d-flex justify-content-between mt-3">
                                                        <Button color="primary" type="submit" disabled={patchisEndShiftLoading || isLoading}>
                                                            {patchisEndShiftLoading ? "Loading..." : "Save"}
                                                        </Button>
                                                    </div>
                                                </Form>
                                            )}
                                    </CardBody>
                                </Card>
                            }

                            {endShiftListView &&
                                <Card className="mt-2">
                                    <CardHeader className="d-flex justify-content-between">
                                        <h4>End of Shift Review</h4>

                                        {reportData?.report_status_name !== "Submitted" && !cardView && endShiftListView && (
                                            <Button
                                                className="btn btn-soft-info btn-md shadow-none"
                                                onClick={() => {
                                                    setEndShiftListView(true);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                        )}
                                    </CardHeader>
                                    <CardBody>
                                        {isLoading ? (
                                            <Spinner color="primary" />
                                        )
                                            // : reportData?.checklist_questionnaire?.length === 0 ? (
                                            //     <div>No checklist questions available</div>
                                            // ) 
                                            :
                                            (
                                                <table className="table table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: "70%" }}>Question</th>
                                                            <th style={{ width: "30%" }}>Answer</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reportData?.end_of_shift_questionnaire?.map((q: any) => (
                                                            <tr key={q.id}>
                                                                <td style={{ width: "90%" }}>{q.question}</td>
                                                                <td style={{ width: "10%" }}>
                                                                    <Badge
                                                                        color={q.answer === "Yes" ? "success" : "danger"}
                                                                    >
                                                                        {q.answer}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>

                                            )}
                                    </CardBody>
                                </Card>
                            }


                            <Card>
                                <CardHeader className="d-flex justify-content-between">
                                    <h4>Observations & Action Required</h4>
                                    {reportData?.report_status_name !== "Submitted" && !cardView &&
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                observationFormik.resetForm();
                                                setEditingReportId(null);
                                                observationToggleModal();
                                            }}
                                        >
                                            Add Observation
                                        </Button>
                                    }
                                </CardHeader>

                                <CardBody>
                                    {reportData?.observation && reportData?.observation?.length > 0 ? (
                                        <div className="d-flex flex-column gap-3">
                                            {reportData?.observation.map((item: any, index: number) => (
                                                <Card key={index}>
                                                    <CardBody>

                                                        <Row>

                                                            <Col md={6}>
                                                                <div className="p-2 bg-light rounded mb-2">
                                                                    <h6 className="mb-1"><strong>Observation</strong></h6>
                                                                    <p className="mb-1">{item.observation}</p>
                                                                </div>
                                                            </Col>
                                                            <Col md={6}>
                                                                <div className="p-2 bg-light rounded mb-2">
                                                                    <div className="d-flex justify-content-between align-items-start">
                                                                        <div>
                                                                            <h6 className="mb-1"><strong>Action Required</strong></h6>
                                                                            <p className="mb-1">{item.action_required}</p>

                                                                        </div>
                                                                        {reportData?.report_status_name !== "Submitted" && !cardView &&

                                                                            <Button
                                                                                color="info"
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    setModalFormData(item);
                                                                                    setEditingReportId(item.id);
                                                                                    observationToggleModal();
                                                                                }}
                                                                            >
                                                                                Edit
                                                                            </Button>
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </CardBody>
                                                </Card>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center">
                                            <p>No risk observations found.</p>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>


                            {/* Modal for adding/editing mitigation reports */}
                            <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
                                <ModalHeader toggle={toggleModal}>
                                    {editingReportId ? "Edit Mitigation Report" : "Add Mitigation Report"}
                                </ModalHeader>
                                <ModalBody>
                                    <Form onSubmit={practiceFormik.handleSubmit}>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Label htmlFor="activity">Activity <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="textarea"
                                                    row={3}
                                                    id="activity"
                                                    name="activity"
                                                    value={practiceFormik.values.activity}
                                                    onChange={practiceFormik.handleChange}
                                                    invalid={practiceFormik.touched.activity && !!practiceFormik.errors.activity}
                                                />
                                                {practiceFormik.touched.activity && practiceFormik.errors.activity && (
                                                    <FormFeedback>{practiceFormik.errors.activity}</FormFeedback>
                                                )}
                                            </Col>
                                            <Col md={6}>
                                                <Label htmlFor="activity_time">Activity Time <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="time"
                                                    id="activity_time"
                                                    name="activity_time"
                                                    value={practiceFormik.values.activity_time}
                                                    onChange={practiceFormik.handleChange}
                                                    invalid={practiceFormik.touched.activity_time && !!practiceFormik.errors.activity_time}
                                                />
                                                {practiceFormik.touched.activity_time && practiceFormik.errors.activity_time && (
                                                    <FormFeedback>{practiceFormik.errors.activity_time}</FormFeedback>
                                                )}
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Label htmlFor="hazard">Hazard <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="textarea"
                                                    row={3} id="hazard"
                                                    name="hazard"
                                                    value={practiceFormik.values.hazard}
                                                    onChange={practiceFormik.handleChange}
                                                    invalid={practiceFormik.touched.hazard && !!practiceFormik.errors.hazard}
                                                />
                                                {practiceFormik.touched.hazard && practiceFormik.errors.hazard && (
                                                    <FormFeedback>{practiceFormik.errors.hazard}</FormFeedback>
                                                )}
                                            </Col>
                                            <Col md={6}>
                                                <Label htmlFor="safety_provisions">Safety Provisions <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="textarea"
                                                    row={3}
                                                    id="safety_provisions"
                                                    name="safety_provisions"
                                                    value={practiceFormik.values.safety_provisions}
                                                    onChange={practiceFormik.handleChange}
                                                    invalid={practiceFormik.touched.safety_provisions && !!practiceFormik.errors.safety_provisions}
                                                />
                                                {practiceFormik.touched.safety_provisions && practiceFormik.errors.safety_provisions && (
                                                    <FormFeedback>{practiceFormik.errors.safety_provisions}</FormFeedback>
                                                )}
                                            </Col>
                                            
                                        </Row>

                                        <Row className="mb-3">
                                            {/* <Col md={6}>
                                                <Label htmlFor="hazard_time">Hazard Time <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="time"
                                                    id="hazard_time"
                                                    name="hazard_time"
                                                    value={practiceFormik.values.hazard_time}
                                                    onChange={practiceFormik.handleChange}
                                                    invalid={practiceFormik.touched.hazard_time && !!practiceFormik.errors.hazard_time}
                                                />
                                                {practiceFormik.touched.hazard_time && practiceFormik.errors.hazard_time && (
                                                    <FormFeedback>{practiceFormik.errors.hazard_time}</FormFeedback>
                                                )}
                                            </Col> */}
                                           
                                            {/* <Col md={6}>
                                                <Label htmlFor="safety_provisions_time">Safety Provisions Time <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="time"
                                                    id="safety_provisions_time"
                                                    name="safety_provisions_time"
                                                    value={practiceFormik.values.safety_provisions_time}
                                                    onChange={practiceFormik.handleChange}
                                                    invalid={practiceFormik.touched.safety_provisions_time && !!practiceFormik.errors.safety_provisions_time}
                                                />
                                                {practiceFormik.touched.safety_provisions_time && practiceFormik.errors.safety_provisions_time && (
                                                    <FormFeedback>{practiceFormik.errors.safety_provisions_time}</FormFeedback>
                                                )}
                                            </Col> */}
                                        </Row>

                                        <div className="modal-footer">
                                            <Button color="secondary" onClick={toggleModal}>
                                                Cancel
                                            </Button>
                                            <Button
                                                color="primary"
                                                type="submit"
                                                disabled={isCreatingbyId || patchReportRowByIdloading}
                                            >
                                                {isCreatingbyId || patchReportRowByIdloading ? (
                                                    <Spinner size="sm" />
                                                ) : editingReportId ? "Update" : "Save"}
                                            </Button>
                                        </div>
                                    </Form>
                                </ModalBody>
                            </Modal>



                            <Modal isOpen={observationModalOpen} toggle={observationToggleModal} size="lg">
                                <ModalHeader toggle={observationToggleModal}>
                                    Risk Observation Information
                                </ModalHeader>
                                <ModalBody>
                                    <Form onSubmit={observationFormik.handleSubmit}>
                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <Label htmlFor="observation">Observation <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="textarea"
                                                    rows={4}
                                                    name="observation"
                                                    id="observation"
                                                    value={observationFormik.values.observation}
                                                    onChange={observationFormik.handleChange}
                                                    onBlur={observationFormik.handleBlur}
                                                    className="form-control"
                                                    invalid={
                                                        observationFormik.touched.observation &&
                                                        !!observationFormik.errors.observation
                                                    }
                                                />
                                                {observationFormik.touched.observation && observationFormik.errors.observation ? (
                                                    <div className="text-danger">{observationFormik.errors.observation}</div>
                                                ) : null}
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <Label htmlFor="action_required">Action Required <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="textarea"
                                                    rows={4}
                                                    name="action_required"
                                                    id="action_required"
                                                    value={observationFormik.values.action_required}
                                                    onChange={observationFormik.handleChange}
                                                    onBlur={observationFormik.handleBlur}
                                                    className="form-control"
                                                    invalid={
                                                        observationFormik.touched.action_required &&
                                                        !!observationFormik.errors.action_required
                                                    }
                                                />
                                                {observationFormik.touched.action_required && observationFormik.errors.action_required ? (
                                                    <div className="text-danger">{observationFormik.errors.action_required}</div>
                                                ) : null}
                                            </Col>
                                        </Row>

                                        <div className="modal-footer">
                                            <Button color="light" onClick={observationToggleModal}>
                                                Cancel
                                            </Button>
                                            <Button
                                                color="primary"
                                                type="submit"
                                                disabled={patchRiskObservationRowByIdloading || isObservationCreatingbyId}
                                            >
                                                {patchRiskObservationRowByIdloading || isObservationCreatingbyId ?
                                                    <Spinner size="sm" /> : "Save"
                                                }
                                            </Button>
                                        </div>
                                    </Form>
                                </ModalBody>
                            </Modal>
                        </div>}
                    </Col>
                    {reportData?.report_status_name !== "Submitted" && !cardView &&

                        <RiskAssessmentSubmit reportId={reportId} location={location} />
                    }
                </Row>
                <ValidationAlertStatic
                    modalBackDrop={modalBackDrop}
                    setModalBackDrop={setModalBackDrop}
                    validationMessages={validationMessages}
                    previousPageNavLink="/risk-assessment-list"
                />
            </Container>
        </div>
    )
}



export default RiskAssessmentForm;
